import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  ROTULO_ORIGEM, diaMes, pontosDaLinha, diasSemDado, textoResumo, formatarValor,
} from './metasHistorico';

// Metas de bonificação dia a dia (Adnéia, 30/10/2026): ao clicar numa caixa, os últimos 30 dias
// (só úteis, até ontem) num gráfico de linha, com a meta tracejada e os dias fora em vermelho.
// Ponto cheio = foto do dia; vazio = remontado pelo que o ELO guarda. Os números vêm prontos da
// rota /api/dashboard/v2/metas-historico; a tela só desenha. Sem dado, "?".
const API_URL = process.env.REACT_APP_BACKEND_URL;
const COR_LINHA = '#3b82f6';
const COR_FORA = '#dc2626';
const COR_DENTRO = '#059669';
const VALE_MS = 5 * 60 * 1000;
// A aba Performance é remontada a cada render do Dashboard: a leitura fica guardada aqui.
let guardado = { em: 0, dados: null };

function Ponto({ cx, cy, payload }) {
  if (cx == null || cy == null || !payload || payload.valor == null) return null;
  const cor = payload.fora ? COR_FORA : COR_DENTRO;
  return (
    <circle cx={cx} cy={cy} r={4} stroke={cor} strokeWidth={2}
      style={{ fill: payload.origem === 'foto' ? cor : 'hsl(var(--background))' }} />
  );
}

function Dica({ active, payload, linha }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  const situacao = p.ok === true ? 'Bateu a meta' : p.ok === false ? 'Fora da meta' : 'Sem dado';
  const cor = p.ok === true ? 'text-emerald-600' : p.ok === false ? 'text-red-600' : 'text-muted-foreground';
  return (
    <div className="rounded-md border bg-background p-2 text-xs shadow-sm max-w-[260px] space-y-0.5">
      <div className="font-semibold">{p.dia}</div>
      <div>{linha.rotulo}: <b>{formatarValor(p.valor, linha.unidade)}</b> · meta {linha.texto_meta}</div>
      <div className={cor}>{situacao}{p.origem ? ` · ${ROTULO_ORIGEM[p.origem] || p.origem}` : ''}</div>
      {p.porque && <div className="text-muted-foreground">{p.porque}</div>}
    </div>
  );
}

export default function MetaHistorico({ chave, onFechar }) {
  const { getAuthHeader } = useAuth();
  const [dados, setDados] = useState(guardado.dados);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    if (guardado.dados && Date.now() - guardado.em < VALE_MS) {
      setDados(guardado.dados);
      return undefined;
    }
    let vivo = true;
    axios.get(`${API_URL}/api/dashboard/v2/metas-historico?dias=30`, { headers: getAuthHeader() })
      .then((r) => {
        guardado = { em: Date.now(), dados: r.data };
        if (vivo) setDados(r.data);
      })
      .catch(() => { if (vivo) setErro('Não consegui ler os últimos 30 dias agora. Tente de novo em alguns minutos.'); });
    return () => { vivo = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const meta = dados && dados.metas ? dados.metas[chave] : null;
  const semDado = meta ? diasSemDado(meta.pontos) : [];
  const horaFoto = (dados && dados.hora_foto) || 18;

  return (
    <Card className="mt-3 border-orange-200 dark:border-orange-900" data-testid="meta-historico">
      <CardHeader className="p-3 pb-1 flex flex-row items-start justify-between gap-2 space-y-0">
        <div className="min-w-0">
          <CardTitle className="text-base">{meta ? meta.rotulo : 'Meta'} · últimos 30 dias</CardTitle>
          <p className="text-xs text-muted-foreground">
            Só dias úteis, até ontem.{meta ? ` ${textoResumo(meta.resumo)}.` : ''}
          </p>
        </div>
        <Button type="button" size="sm" variant="ghost" onClick={onFechar} aria-label="Fechar o gráfico">
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="p-3 pt-1 space-y-3">
        {erro && <p className="text-sm text-red-600">{erro}</p>}
        {!dados && !erro && <p className="text-sm text-muted-foreground">Carregando os últimos 30 dias…</p>}
        {meta && (
          <div className={`grid gap-4 ${meta.linhas.length > 1 ? 'md:grid-cols-2' : ''}`}>
            {meta.linhas.map((l) => {
              const serie = pontosDaLinha(meta.pontos, l.rotulo);
              const temValor = serie.some((s) => s.valor != null);
              return (
                <div key={l.rotulo} className="min-w-0">
                  <div className="flex items-baseline justify-between gap-2 text-sm">
                    <span className="font-semibold">{l.rotulo}</span>
                    <span className="text-xs text-muted-foreground">meta: {l.texto_meta}</span>
                  </div>
                  {temValor ? (
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={serie} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="dia" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                          <YAxis tick={{ fontSize: 10 }} domain={[0, 'auto']} />
                          <Tooltip content={<Dica linha={l} />} />
                          <ReferenceLine y={l.meta} stroke={COR_FORA} strokeDasharray="5 4" ifOverflow="extendDomain"
                            label={{ value: `meta ${formatarValor(l.meta, l.unidade)}`, position: 'insideTopRight', fontSize: 10, fill: COR_FORA }} />
                          <Line type="monotone" dataKey="valor" stroke={COR_LINHA} strokeWidth={2}
                            connectNulls={false} dot={<Ponto />} activeDot={{ r: 6 }} isAnimationActive={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground py-6 text-center">
                      Ainda sem dado nesta linha: ela começa a encher com a foto das {horaFoto}h dos dias úteis.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {meta && semDado.length > 0 && (
          <p className="text-xs text-muted-foreground">Sem dado ("?"): {semDado.join(', ')}.</p>
        )}
        {dados && (
          <p className="text-[11px] text-muted-foreground">
            Ponto cheio: foto do dia, às {horaFoto}h. Ponto vazio: remontado pelo que o ELO guarda (passe o
            mouse para ver se é aproximado). Vermelho: fora da meta.
            {dados.foto_desde ? ` Foto do dia guardada desde ${diaMes(dados.foto_desde)}.` : ` A foto do dia começa hoje, às ${horaFoto}h.`}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
